import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import * as Sentry from '@sentry/react';

export interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

type MetricRatingThresholds = {
  good: number;
  needsImprovement: number;
};

const thresholds: Record<string, MetricRatingThresholds> = {
  LCP: { good: 2500, needsImprovement: 4000 },
  FID: { good: 100, needsImprovement: 300 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  TTFB: { good: 800, needsImprovement: 1800 },
  FCP: { good: 1800, needsImprovement: 3000 },
  INP: { good: 200, needsImprovement: 500 },
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = thresholds[name];
  if (!threshold) return 'good';
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

function sendToSentry(metric: WebVitalMetric) {
  Sentry.addBreadcrumb({
    category: 'web-vitals',
    message: `${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`,
    level: metric.rating === 'poor' ? 'warning' : 'info',
    data: {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
    },
  });

  if (metric.rating === 'poor') {
    Sentry.captureMessage(`Poor Web Vital: ${metric.name}`, {
      level: 'warning',
      tags: {
        metric: metric.name,
        rating: metric.rating,
      },
      extra: {
        value: metric.value,
        thresholds: thresholds[metric.name],
      },
    });
  }
}

function handleMetric(name: string, metric: { value: number; delta: number; id: string }) {
  const webVital: WebVitalMetric = {
    name,
    value: metric.value,
    rating: getRating(name, metric.value),
    delta: metric.delta,
    id: metric.id,
  };

  sendToSentry(webVital);

  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${name}:`, {
      value: `${metric.value.toFixed(2)}ms`,
      rating: webVital.rating,
    });
  }
}

export function initWebVitals() {
  onLCP((metric) => handleMetric('LCP', metric));
  onINP((metric) => handleMetric('INP', metric));
  onCLS((metric) => handleMetric('CLS', metric));
  onTTFB((metric) => handleMetric('TTFB', metric));
  onFCP((metric) => handleMetric('FCP', metric));
}

export { getRating };
